import { Card, Checkbox, CheckboxGroup, Description, Label } from '@heroui/react';

const BORROW_VALUE = 'borrow';
const RENT_VALUE = 'rent';

interface UsageTypeCheckboxGroupProps {
  label: string;
  description?: string;
  borrowLabel?: string;
  rentLabel?: string;
  borrowSelected: boolean;
  rentSelected: boolean;
  onChange: (borrow: boolean, rent: boolean) => void;
  /** Em drawers de data/hora usa card; no drawer de filtros, layout plano. */
  inCard?: boolean;
  cardVariant?: 'default' | 'secondary';
  orientation?: 'horizontal' | 'vertical';
  /** Impede desmarcar o último item (ex.: filtros de vagas disponíveis). */
  requireAtLeastOne?: boolean;
  isDisabled?: boolean;
}

function toSelectedValues(borrowSelected: boolean, rentSelected: boolean): string[] {
  const values: string[] = [];
  if (borrowSelected) values.push(BORROW_VALUE);
  if (rentSelected) values.push(RENT_VALUE);
  return values;
}

export function UsageTypeCheckboxGroup({
  label,
  description,
  borrowLabel = 'Empréstimo',
  rentLabel = 'Aluguel',
  borrowSelected,
  rentSelected,
  onChange,
  inCard = true,
  cardVariant = 'default',
  orientation = 'horizontal',
  requireAtLeastOne = false,
  isDisabled = false,
}: UsageTypeCheckboxGroupProps) {
  const groupClassName =
    orientation === 'horizontal'
      ? 'flex flex-row flex-wrap gap-4 [&_[data-slot=checkbox]]:mt-0'
      : 'gap-2 [&_[data-slot=checkbox]]:mt-0';

  const fields = (
    <CheckboxGroup
      variant="secondary"
      className={groupClassName}
      aria-label={label}
      isDisabled={isDisabled}
      value={toSelectedValues(borrowSelected, rentSelected)}
      onChange={(values) => {
        const borrow = values.includes(BORROW_VALUE);
        const rent = values.includes(RENT_VALUE);
        if (requireAtLeastOne && !borrow && !rent) return;
        onChange(borrow, rent);
      }}
    >
      <Checkbox value={BORROW_VALUE}>
        <Checkbox.Content>
          <Checkbox.Control>
            <Checkbox.Indicator />
          </Checkbox.Control>
          {borrowLabel}
        </Checkbox.Content>
      </Checkbox>
      <Checkbox value={RENT_VALUE}>
        <Checkbox.Content>
          <Checkbox.Control>
            <Checkbox.Indicator />
          </Checkbox.Control>
          {rentLabel}
        </Checkbox.Content>
      </Checkbox>
    </CheckboxGroup>
  );

  if (!inCard) {
    return (
      <section className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <Label className="text-base font-semibold">{label}</Label>
          {description ? <Description>{description}</Description> : null}
        </div>
        {fields}
      </section>
    );
  }

  return (
    <Card variant={cardVariant}>
      <Card.Header className="border-b border-separator pb-3">
        <Card.Title className="text-base font-semibold">{label}</Card.Title>
        {description ? <Card.Description>{description}</Card.Description> : null}
      </Card.Header>
      <Card.Content className="pt-3">{fields}</Card.Content>
    </Card>
  );
}
